import React, { useState } from 'react';
import { LanguageSwitcher } from '../shared';
import './GDPRConsentModal.css';

interface GDPRConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConsentGiven: (consent: boolean) => void;
  language?: 'en' | 'el'; // Add language prop
}

const GDPRConsentModal: React.FC<GDPRConsentModalProps> = ({
  isOpen,
  onClose,
  onConsentGiven,
  language
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'el'>(language || 'el'); // Use prop language or default to Greek

  const handleLanguageChange = (language: 'en' | 'el') => {
    setSelectedLanguage(language);
  };

  const handleConsent = (consent: boolean) => {
    onConsentGiven(consent);
  };

  if (!isOpen) return null;

  const content = {
    en: {
      title: "Data Protection & Privacy Consent",
      description: "Before you begin this medical questionnaire, we need your informed consent for data processing in accordance with GDPR regulations.",
      details: [
        "Your responses will be used exclusively for medical assessment and treatment purposes.",
        "All data will be stored securely and confidentially in our medical database.",
        "Only authorized medical personnel will have access to your information.",
        "You have the right to withdraw your consent at any time by contacting your doctor.",
        "If you have any questions or concerns, please discuss them with your doctor before proceeding.",
        "If you decline consent, you will not be able to complete the questionnaire and will be redirected to an information page."
      ],
      accept: "I Accept and Proceed",
      decline: "I Decline"
    },
    el: {
      title: "Συναίνεση Προστασίας & Απορρήτου Δεδομένων",
      description: "Πριν ξεκινήσετε αυτό το ιατρικό ερωτηματολόγιο, χρειαζόμαστε τη συναίνεσή σας για την επεξεργασία δεδομένων σύμφωνα με τους κανόνες GDPR.",
      details: [
        "Οι απαντήσεις σας θα χρησιμοποιηθούν αποκλειστικά για σκοπούς ιατρικής αξιολόγησης και θεραπείας.",
        "Όλα τα δεδομένα θα αποθηκευτούν με ασφάλεια στη βάση δεδομένων μας.",
        "Μόνο εξουσιοδοτημένο ιατρικό προσωπικό θα έχει πρόσβαση στις πληροφορίες σας.",
        "Έχετε το δικαίωμα να αποσύρετε τη συναίνεσή σας οποιαδήποτε στιγμή επικοινωνώντας με τον γιατρό σας.",
        "Εάν έχετε οποιεσδήποτε ερωτήσεις ή ανησυχίες, παρακαλώ συζητήστε τις με τον γιατρό σας πριν προχωρήσετε.",
        "Εάν δεν δώσετε τη συναίνεσή σας, δεν θα μπορείτε να συμπληρώσετε το ερωτηματολόγιο και θα μεταφερθείτε σε μια σελίδα πληροφοριών."
      ],
      accept: "Αποδέχομαι και Συνεχίζω",
      decline: "Δεν Αποδέχομαι"
    }
  };

  const t = content[selectedLanguage as keyof typeof content];

  return (
    <div className="gdpr-modal-overlay">
      <div className="gdpr-modal">
        <div className="gdpr-modal-header">
          <h2>{t.title}</h2>
          <LanguageSwitcher
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            variant="default"
          />
        </div>
        
        <div className="gdpr-modal-content">
          <p className="gdpr-description">{t.description}</p>
          <div className="gdpr-details">
            <h6>{selectedLanguage === 'el' ? 'Σημαντικές Πληροφορίες:' : 'Important Information:'}</h6>
            <ul>
              {t.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="gdpr-modal-actions">
          <button 
            className="gdpr-button decline" 
            onClick={() => handleConsent(false)}
          >
            {t.decline}
          </button>
          <button 
            className="gdpr-button accept" 
            onClick={() => handleConsent(true)}
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GDPRConsentModal;
